import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { LiveUpdatesProvider } from './context/LiveUpdatesContext';
import { Toaster } from 'react-hot-toast';
import ProtectedRoute from './utils/ProtectedRoute';
import { Login, DashboardLayout, LoadingSpinner, RootRedirect, DashboardMenuRoute, NotForOwnerRoute } from './components';
import {
  Dashboard,
  CommonDashboard,
  Organizations,
  CreateOrganization,
  Settings,
  Menus,
  CreateEditMenu,
  ComingSoon,
  ELearning,
  Events,
  Notifications,
  OrganizationPlans,
  SetPin,
  ForgotPin,
  AuditLogs,
  ContactSubmissions,
  Mailbox,
  Support
} from './pages';
import ManagerDashboard from './pages/carehome_manager/Dashboard';
// import ManagerEmployees from './pages/carehome_manager/Employees'; // Consolidated into StaffList
import EmployeeDetails from './pages/carehome_manager/EmployeeDetails';
import ManagerResidences from './pages/carehome_manager/residences/Residences';
import CreateEmployee from './pages/carehome_manager/CreateEmployee';
import CreateResidence from './pages/carehome_manager/residences/CreateResidence';
import './App.css';
import StaffList from './pages/carehome_manager/StaffList';
import CreateEditStaff from './pages/carehome_manager/CreateEditStaff';
import OrganisationSettings from './pages/carehome_manager/OrganisationSettings';
import StaffView from './pages/carehome_manager/StaffView';
import StaffCompliance from './pages/carehome_manager/StaffCompliance';
import Recruitment from './pages/carehome_manager/Recruitment';
import Payroll from './pages/carehome_manager/Payroll';
import CandidateOnboardingForm from './pages/CandidateOnboardingForm';
import PreInterviewForm from './pages/PreInterviewForm';
import ReferenceForm from './pages/ReferenceForm';
import CareAssessmentForm from './pages/CareAssessmentForm';
import Training from './pages/carehome_manager/Training';
import AuditsPolicies from './pages/carehome_manager/quality/AuditsPolicies';
import StaffDashboard from './pages/staffs/dashbaord';
import ResidenceDetails from './pages/carehome_manager/residences/ResidenceDetails';
import EnquiryList from './pages/carehome_manager/residences/EnquiryList';
import EnquiriesAssessments from './pages/carehome_manager/enquiries/EnquiriesAssessments';
import EnquiryDetail from './pages/carehome_manager/enquiries/EnquiryDetail';
import ConvertClient from './pages/carehome_manager/residences/ConvertClient';
import CareNotes from './pages/carehome_manager/care_notes/CareNotes';
import DutyRoster from './pages/carehome_manager/DutyRoster';
import { ScheduleScreen as DomiciliarySchedule, CarePackagesScreen } from './pages/carehome_manager/domiciliary_roster';
import TodoList from './pages/carehome_manager/TodoList';
import CarePlanner from './pages/carehome_manager/care_planner/CarePlanner';
import Announcements from './pages/carehome_manager/Announcements';
import StaffReports from './pages/carehome_manager/StaffReports';
import EvacuationPlan from './pages/carehome_manager/EvacuationPlan';
import Messages from './pages/carehome_manager/Messages';
import MyLeave from './pages/leave/MyLeave';
import LeaveApprovals from './pages/carehome_manager/LeaveApprovals';
// Staffing agencies: the admin's list, the care home's Agencies section, and
// the agency's own workspace (agency administrators only — see AppGuard).
import AdminAgencies from './pages/Agencies';
import HomeAgencies from './pages/carehome_manager/agencies/Agencies';
import AgencyOverview from './pages/agency/AgencyOverview';
import AgencyRota from './pages/agency/AgencyRota';
import AgencyRequests from './pages/agency/AgencyRequests';
import AgencyBookings from './pages/agency/AgencyBookings';
import AgencyStaff from './pages/agency/AgencyStaff';
import AgencyHours from './pages/agency/AgencyHours';
import AgencyHomes from './pages/agency/AgencyHomes';
import AgencyAccount from './pages/agency/AgencyAccount';

// Agency workspace pages, one route each under /agency.
const AGENCY_ROUTES = [
  ['/agency/overview', AgencyOverview],
  ['/agency/requests', AgencyRequests],
  ['/agency/bookings', AgencyBookings],
  ['/agency/rota', AgencyRota],
  ['/agency/staff', AgencyStaff],
  ['/agency/hours', AgencyHours],
  ['/agency/care-homes', AgencyHomes],
  ['/agency/account', AgencyAccount],
];

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Routes>
      {/* Login Route */}
      <Route path="/login" element={<Login />} />
      <Route path="/set-pin/:token" element={<SetPin />} />
      <Route path="/forgot-pin" element={<ForgotPin />} />
      {/* The two candidate forms. Public — the emailed token is the credential,
          so these must stay outside ProtectedRoute.

          Step 1, sent before an interview is booked: CV, Right to Work, proof
          of NI, an existing DBS, employment history and referees. */}
      <Route path="/pre-interview/:token" element={<PreInterviewForm />} />
      {/* A REFEREE's form. Not the candidate's — a different person entirely,
          who sees only the applicant's name and the questions. */}
      <Route path="/reference/:token" element={<ReferenceForm />} />
      {/* An ASSESSOR's form — somebody asked to assess a person's care needs
          before admission. No account, one-time emailed link. */}
      <Route path="/care-assessment/:token" element={<CareAssessmentForm />} />
      {/* Step 3, sent after a successful interview: address, next of kin and
          bank details. Nothing uploads here. */}
      <Route path="/onboarding/:token" element={<CandidateOnboardingForm />} />

      {/* Dashboard Routes - Protected */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CommonDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Organizations Routes */}
      <Route
        path="/organizations"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Organizations />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Admin > Agencies — the platform admin creates staffing agencies. */}
      <Route
        path="/agencies"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdminAgencies />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Staff > Agencies — a care home booking agency cover. */}
      <Route
        path="/manager/agencies"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <HomeAgencies />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* The agency workspace. DashboardLayout's WorkspaceBoundary keeps
          agency administrators here and everybody else out. */}
      <Route path="/agency" element={<Navigate to="/agency/overview" replace />} />
      {AGENCY_ROUTES.map(([path, Page]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <Page />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      ))}

      <Route
        path="/organizations/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateOrganization />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/organizations/:id/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateOrganization />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/organizations/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateOrganization />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/plans"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrganizationPlans />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AuditLogs />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/contact-submissions"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ContactSubmissions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/mailbox"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Mailbox />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Support />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Settings Route */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Settings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Notifications />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Menu Management Routes - Admin Only */}
      <Route
        path="/menus"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Menus />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/menus/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEditMenu />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/menus/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEditMenu />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Staff Management Routes - Manager Only */}
      <Route
        path="/manager/staffs"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StaffList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/staffs/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEditStaff />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Recruitment & Onboarding — candidates before they become staff. */}
      <Route
        path="/manager/recruitment"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Recruitment />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/staff-compliance"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StaffCompliance />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Payroll — hourly rates and the worked-hours export. Part of the Staff
          section; the page itself is manager-only and the API enforces that. */}
      <Route
        path="/manager/payroll"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Payroll />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/training"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Training />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Audits & policies — quality audits, their follow-up actions and the
          versioned policy library. An assignable module like any other:
          managers hold it by role, and anybody else once they are granted the
          Audits & Policies menu. The API applies the same isManager() gate.

          The section is carried in the URL so an audit can be linked to and
          the browser's back button behaves — see AuditsPolicies.js. */}
      {[
        ['/manager/audits-policies', 'audits'],
        ['/manager/audits-policies/actions', 'actions'],
        ['/manager/audits-policies/audits/:auditId', 'audit'],
        ['/manager/audits-policies/policies', 'policies'],
        ['/manager/audits-policies/policies/new', 'policies-editor'],
        ['/manager/audits-policies/policies/:policyId/edit', 'policies-editor'],
      ].map(([path, section]) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AuditsPolicies section={section} />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />
      ))}

      <Route
        path="/manager/staffs/:staffId/view"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StaffView />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/staffs/:staffId/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEditStaff />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/staffs/:staffId/approve"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEditStaff />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/todos"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <TodoList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Care Planner — care homes only (the page and the API both refuse a
          domiciliary organisation; the sidebar hides it via careHomeOnly). */}
      <Route
        path="/manager/care-planner"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CarePlanner />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* My leave — every role; reached from the profile dropdown. Not under
          /manager because it isn't a manager screen. */}
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            {/* Not for the account owner, who takes no leave. */}
            <NotForOwnerRoute>
              <DashboardLayout>
                <MyLeave />
              </DashboardLayout>
            </NotForOwnerRoute>
          </ProtectedRoute>
        }
      />

      {/* Leave & Approval — managers only; the page itself checks the role. */}
      <Route
        path="/manager/leave-approvals"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <LeaveApprovals />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Care Home Manager Routes */}
      <Route
        path="/manager/organisation-settings"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <OrganisationSettings />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* The dashboard overview is a granted module now (see the Dashboard tick
          in Add Staff > Module Access), so it is gated like the rest and cannot
          be reached by URL. Managers always hold it. */}
      <Route
        path="/manager/dashboard"
        element={
          <ProtectedRoute>
            <DashboardMenuRoute>
              <DashboardLayout>
                <ManagerDashboard />
              </DashboardLayout>
            </DashboardMenuRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/duty-roster"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DutyRoster />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/domiciliary-schedule"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <DomiciliarySchedule />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/care-packages"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CarePackagesScreen />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/events"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Events />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/announcements"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Announcements />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Private staff messaging — unrelated to the Ask Patch assistant. */}
      <Route
        path="/manager/messages"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Messages />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/staffs-report"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StaffReports />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/evacuation-plan"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EvacuationPlan />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/employees"
        element={<Navigate to="/manager/staffs" replace />}
      />

      <Route
        path="/manager/employees/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEmployee />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/employees/:id/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateEmployee />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/employees/:id/details"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EmployeeDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/e-learning"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ELearning />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/residences"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ManagerResidences />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Add a client / resident from scratch: the same 6-step form the
          conversion uses, with nothing to convert. The Enquiry List is reached
          from Enquiries & Assessments only. */}
      <Route
        path="/manager/residences/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ConvertClient createMode />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/residences/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateResidence />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/residences/:id/create"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CreateResidence />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/residences/:id/edit"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ConvertClient />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/residences/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ResidenceDetails />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Care Records — every resident's care notes in one feed, with the
          phone's "All residents" picker. Two sidebar entries under the Care
          Records group: Care Notes, and Handovers (the same page scoped to
          handover notes). Same isManager() gate in the API. */}
      <Route
        path="/manager/care-notes"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CareNotes />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/handovers"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CareNotes key="handovers" handoverOnly />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Enquiries & Assessments — the step BEFORE the Enquiry List. An
          enquiry is taken here, assessed by an outside assessor through an
          emailed link, and decided by the manager. Approved and planned people
          are handed over to /manager/enquiries for the existing conversion. */}
      <Route
        path="/manager/enquiries-assessments"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EnquiriesAssessments />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/enquiries-assessments/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EnquiryDetail />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/enquiries"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EnquiryList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/manager/enquiries/convert"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ConvertClient />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/staffs/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <StaffDashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      {/* Redirect root based on user role */}
      <Route path="/" element={<ProtectedRoute><RootRedirect /></ProtectedRoute>} />

      {/* Coming Soon - for unimplemented routes */}
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ComingSoon />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

/**
 * Main App with Providers
 */
function App() {
  return (
    <ThemeProvider>
      <Toaster position="top-right" />
      <Router>
        <AuthProvider>
          {/* Live refresh: one shared "anything changed?" check per tab. */}
          <LiveUpdatesProvider>
            <AppContent />
          </LiveUpdatesProvider>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
